import React, { useState, useEffect } from "react";
import { NavLink, useParams } from "react-router-dom";
import { Page, Document, PDFViewer } from "@react-pdf/renderer";
import { DEFAULT_COMPANY, formatDocDate, enhanceLineItemsWithTax } from "../../../../../utils/pdfDocumentUtils";
import {
  arPdfStyles,
  ArDocumentTitle,
  ArDocumentHeader,
  ArBuyerSiteSection,
  ArServicesTable,
  ArAmountInWords,
  ArHsnSummaryTable,
  ArBankSignatureFooter,
  ArPageNumber,
  ArRemarksSection,
} from "../../../../../components/pdf/ArDocumentPdfLayout";

const DebitNoteDocument = ({ allData, company, customer, site, project }) => {
  const debitNote = allData?.debit_note || {};
  const lineItems = enhanceLineItemsWithTax(allData?.line_items || [], debitNote);
  const totalAmount = parseFloat(debitNote.total_amount || 0);

  const detailRows = [
    { label: "Debit Note No.", value: debitNote.doc_no },
    { label: "Dated", value: formatDocDate(debitNote.debit_note_date) },
    {
      label: "Reference No. & Date",
      value: debitNote.original_invoice_no
        ? `${debitNote.original_invoice_no}${debitNote.original_invoice_date ? ` dated ${formatDocDate(debitNote.original_invoice_date)}` : ""}`
        : null,
    },
    { label: "Buyer's Order No.", value: debitNote.buyer_order_no },
    { label: "Dated", value: debitNote.buyer_order_date ? formatDocDate(debitNote.buyer_order_date) : null },
    { label: "Destination", value: debitNote.destination || site?.site_name || project?.project_name },
  ].filter((row) => row.value);

  return (
    <Document>
      <Page size="A4" style={arPdfStyles.page}>
        <ArDocumentTitle
          title="DEBIT NOTE"
          subtitle="(As per Rule 46 of CGST Rules, 2017 & Section 31 of CGST Act, 2017)"
        />
        <ArDocumentHeader company={company} detailRows={detailRows} />
        <ArBuyerSiteSection
          customer={customer}
          site={site}
          project={project}
          placeOfSupply={debitNote.place_of_supply}
        />
        <ArRemarksSection remarks={debitNote.reason ? `Reason: ${debitNote.reason}` : null} />
        <ArServicesTable lineItems={lineItems} doc={debitNote} />
        <ArAmountInWords amount={totalAmount} wordsOverride={debitNote.amount_in_words} />
        <ArHsnSummaryTable enhancedItems={lineItems} doc={debitNote} />
        <ArBankSignatureFooter company={company} docTypeLabel="Debit Note" />
        <ArPageNumber />
      </Page>
    </Document>
  );
};

export default function DebitNotePrint() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [allData, setAllData] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [site, setSite] = useState(null);
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessError, setAccessError] = useState(null);

  useEffect(() => {
    const fetchDebitNoteData = async () => {
      try {
        const token = localStorage.getItem("zTaskAuthToken");
        const API_BASE = process.env.REACT_APP_NODE_SERVICE_BASE_URL || "http://localhost:8081";

        const debitNoteResponse = await fetch(`${API_BASE}/debit-note/api/debit-notes/${id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (debitNoteResponse.status === 403) {
          const err = await debitNoteResponse.json();
          setAccessError(err.message || "PDF is available only after the debit note is approved.");
          return;
        }

        if (!debitNoteResponse.ok) {
          throw new Error("Failed to fetch debit note");
        }

        const pdfData = await debitNoteResponse.json();
        setAllData(pdfData);
        setCustomer(pdfData.customer);
        setSite(pdfData.site);
        setProject(pdfData.project);
        setCompany(DEFAULT_COMPANY);
      } catch (error) {
        console.error("Error fetching debit note data:", error);
        alert(`Error loading debit note: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchDebitNoteData();
    }
  }, [id]);

  if (isLoading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>Loading debit note data...</p>
      </div>
    );
  }

  if (accessError) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p style={{ color: "#c62828" }}>{accessError}</p>
        <NavLink to="/ar/debit-notes">
          <button style={{ padding: "8px 16px", background: "#007bff", color: "white", border: "none", borderRadius: "4px" }}>
            Back to Debit Notes
          </button>
        </NavLink>
      </div>
    );
  }

  if (!allData) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>Debit note not found.</p>
        <NavLink to="/ar/debit-notes">
          <button style={{ padding: "8px 16px", background: "#007bff", color: "white", border: "none", borderRadius: "4px" }}>
            Back to Debit Notes
          </button>
        </NavLink>
      </div>
    );
  }

  return (
    <>
      <NavLink to="/ar/debit-notes">
        <button style={{ marginBottom: "10px", padding: "8px 16px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px" }}>
          Back To Debit Notes
        </button>
      </NavLink>
      <PDFViewer style={arPdfStyles.viewer}>
        <DebitNoteDocument allData={allData} company={company} customer={customer} site={site} project={project} />
      </PDFViewer>
    </>
  );
}
